import { moveItemInArray } from '@angular/cdk/drag-drop';
import { createReducer, on } from '@ngrx/store';

import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { IntervalCode } from '@ninety/platform/models/issues/interval-code';
import { Issue } from '@ninety/platform/models/issues/issue';
import { IssueSortField } from '@ninety/platform/models/issues/issues-sort-field';
import { LayoutsIssuesEvents, RocksIssuesEvents, teamAll } from '@ninety/platform/state';

import { IssuesArchiveApiActions, IssuesArchivePageActions } from './archive/archive.actions';
import {
  sortIssuesLocalAndUpdateOrdinalsReducer,
  sortIssuesLocalFromBroadcastReducer,
  sortIssuesLocalOnlyReducer,
  updateOrdinalsOnDragDropReducer,
} from './issues-rocks.reducer';
import { IssuesActions, IssuesSubscriptionsActions } from './issues.actions';
import { issuesEntityAdapter, issuesReceivedEntityAdapter, issuesSentEntityAdapter } from './issues.adapter';
import { IssuesState, LoadingState, issuesInitialState } from './issues.model';
import { INLINE_TEMP_ISSUE_ID } from './issues.selectors';
import { IssuesTrashApiActions, IssuesTrashPageActions } from './trash/trash.actions';

function newIssueBelongsInCurrentTeamList(state: IssuesState, issue: Issue): boolean {
  const teamMatches = state.filters.teamId === teamAll._id || issue.teamId === state.filters.teamId;

  return (
    teamMatches &&
    issue.intervalCode === state.filters.interval &&
    !state.filters.showArchived &&
    !state.ui.isViewingArchivedVto
  );
}

/**
 * Helper function to update an issue in the appropriate list (issues, issuesReceived, or issuesSent).
 * Issue can only be in one list, so this checks which one and updates accordingly.
 */
export function updateIssueInMultipleLists(
  state: IssuesState,
  issueId: string,
  changes: Partial<Issue>,
  additionalStateUpdates?: Partial<IssuesState>
): IssuesState {
  const updatePayload = { id: issueId, changes };

  // Issue can only be in one list, so check which one and update accordingly
  if (state.issues.entities[issueId]) {
    return {
      ...state,
      ...additionalStateUpdates,
      issues: issuesEntityAdapter.updateOne(updatePayload, state.issues),
      ...(state.issueStandalone && state.issueStandalone._id === issueId
        ? { issueStandalone: { ...state.issueStandalone, ...changes } }
        : {}),
    };
  }

  if (state.issuesReceived.entities[issueId]) {
    return {
      ...state,
      ...additionalStateUpdates,
      issuesReceived: issuesReceivedEntityAdapter.updateOne(updatePayload, state.issuesReceived),
      ...(state.issueStandalone && state.issueStandalone._id === issueId
        ? { issueStandalone: { ...state.issueStandalone, ...changes } }
        : {}),
    };
  }

  if (state.issuesSent.entities[issueId]) {
    return {
      ...state,
      ...additionalStateUpdates,
      issuesSent: issuesSentEntityAdapter.updateOne(updatePayload, state.issuesSent),
      ...(state.issueStandalone && state.issueStandalone._id === issueId
        ? { issueStandalone: { ...state.issueStandalone, ...changes } }
        : {}),
    };
  }

  //additionally, if the issue is not found in any list, it might be loaded by itself,
  //for example when we go to a page the issue is not on
  if (state.issueStandalone?._id === issueId) {
    return {
      ...state,
      ...additionalStateUpdates,
      issueStandalone: { ...state.issueStandalone, ...changes },
    };
  }

  // Issue not found in any list or standalone, return state unchanged
  return state;
}

/**
 * Helper function to find an issue by ID from any of the possible locations:
 * issues, issuesReceived, issuesSent, or issueStandalone.
 * Used in reducers for optimistic updates.
 */
export function findIssueById(state: IssuesState, issueId: string): Issue | null {
  return (
    state.issues.entities[issueId] ||
    state.issuesReceived.entities[issueId] ||
    state.issuesSent.entities[issueId] ||
    (state.issueStandalone?._id === issueId ? state.issueStandalone : null)
  );
}

function addVoteToken(state: IssuesState, userId: string, issueId: string): IssuesState['userVotes'] {
  return {
    ...state.userVotes,
    [userId]: [...(state.userVotes[userId] ?? []), issueId],
  };
}

function removeVoteToken(state: IssuesState, userId: string, issueId: string): IssuesState['userVotes'] {
  const voteTokens = [...(state.userVotes[userId] ?? [])];
  const voteIndex = voteTokens.indexOf(issueId);

  if (voteIndex === -1) {
    return state.userVotes;
  }

  voteTokens.splice(voteIndex, 1);

  return {
    ...state.userVotes,
    [userId]: voteTokens,
  };
}

export const issuesStateReducer = createReducer<IssuesState>(
  issuesInitialState,

  // Load Issues
  on(
    IssuesActions.loadTeamIssues,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
      loadingTeamIssues: LoadingState.Loading,
    })
  ),

  on(IssuesActions.loadPlanningBoardIssuesSuccess, (state, { issues, teamId }): IssuesState => {
    return {
      ...state,
      loading: LoadingState.Loaded,
      issues: issuesEntityAdapter.setAll(issues, state.issues),
      totalCount: issues.length,
      filters: {
        ...state.filters,
        teamId: teamId,
        interval: IntervalCode.longTerm,
      },
    };
  }),

  on(IssuesActions.loadTeamIssuesSuccess, (state, { issues, totalCount }): IssuesState => {
    //Case 1: When the list is loaded with new issues and the standalone issue
    // has a value / detail card is open, we set the standalone issue to null
    // because the issue is now in the list, one source of truth

    // Case 2: When the list is loaded with new issues and the state.selectedIssueId was present
    // in the state, but not present in the new list, we set the standalone issue with the issue
    // that is present in the state before the new list is loaded.

    const issueIdsInNewList = new Set(issues.map(issue => issue._id));
    let issueStandalone = state.issueStandalone;

    // Case 1: Clear standalone if it's now in the list
    if (issueStandalone && issueIdsInNewList.has(issueStandalone._id)) {
      issueStandalone = null;
    }

    // Case 2: Move selected issue to standalone if it's not in the new list
    if (state.selectedIssueId && !issueIdsInNewList.has(state.selectedIssueId)) {
      const selectedIssue = state.issues.entities[state.selectedIssueId];
      if (selectedIssue) {
        issueStandalone = selectedIssue;
      }
    }

    return {
      ...state,
      loading: LoadingState.Loaded,
      loadingTeamIssues: LoadingState.Loaded,
      issues: issuesEntityAdapter.setAll(issues, state.issues),
      totalCount,
      issueStandalone,
    };
  }),
  on(
    IssuesActions.loadTeamIssuesFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
      loadingTeamIssues: LoadingState.Error,
    })
  ),
  on(
    IssuesActions.loadUserVotes,
    (state): IssuesState => ({
      ...state,
      userVotesLoaded: false,
    })
  ),
  on(
    IssuesActions.loadUserVotesSuccess,
    (state, { userId, issueIds }): IssuesState => ({
      ...state,
      userVotesLoaded: true,
      userVotes: {
        ...state.userVotes,
        [userId]: [...issueIds],
      },
    })
  ),
  on(
    IssuesActions.loadUserVotesFailure,
    (state): IssuesState => ({
      ...state,
      userVotesLoaded: true,
    })
  ),

  on(IssuesActions.loadReceivedIssuesSuccess, (state, { issues }): IssuesState => {
    return {
      ...state,
      loadingReceived: LoadingState.Loaded,
      issuesReceived: issuesReceivedEntityAdapter.setAll(issues, state.issuesReceived),
    };
  }),

  on(
    IssuesActions.loadReceivedIssuesFailure,
    (state): IssuesState => ({
      ...state,
      loadingReceived: LoadingState.Error,
    })
  ),

  on(IssuesActions.loadSentIssuesSuccess, (state, { issues }): IssuesState => {
    return {
      ...state,
      loadingSent: LoadingState.Loaded,
      issuesSent: issuesSentEntityAdapter.setAll(issues, state.issuesSent),
    };
  }),

  on(
    IssuesActions.loadSentIssuesFailure,
    (state): IssuesState => ({
      ...state,
      loadingSent: LoadingState.Error,
    })
  ),

  on(
    IssuesActions.selectIssue,
    (state, { issueId }): IssuesState => ({
      ...state,
      selectedIssueId: issueId,
      ui: { ...state.ui, showHistory: false },
    })
  ),
  on(
    IssuesActions.setStandaloneIssue,
    IssuesActions.loadStandaloneIssueSuccess,
    (state, { issue }): IssuesState => ({
      ...state,
      issueStandalone: issue,
      selectedIssueId: issue._id,
      ui: { ...state.ui, showHistory: false },
    })
  ),
  // Create Issue
  on(
    IssuesActions.createIssue,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),

  on(IssuesActions.createIssueSuccess, (state, { issue }): IssuesState => {
    //TODO NEXT: move the following filters to effect
    // redo how we create issues in Universal create and dispatch an effect
    // check inline creation, check archived VTO issues and EOS Archived VTO Issues
    // we might want to create a new action to add to state or pass a flag to add from effect

    const shouldAddIssue = newIssueBelongsInCurrentTeamList(state, issue);

    return {
      ...state,
      loading: LoadingState.Loaded,
      ...(shouldAddIssue
        ? {
            issues: issuesEntityAdapter.addOne(issue, state.issues),
            totalCount: state.totalCount + 1,
          }
        : {}),
    };
  }),

  on(IssuesSubscriptionsActions.addIssueFromBroadcast, (state, { issue }): IssuesState => {
    return {
      ...state,
      issues: issuesEntityAdapter.addOne(issue, state.issues),
      totalCount: state.totalCount + 1,
    };
  }),

  on(IssuesSubscriptionsActions.updateIssueFromBroadcast, (state, { issue }): IssuesState => {
    return updateIssueInMultipleLists(state, issue._id, issue);
  }),

  on(
    IssuesActions.createIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),

  on(IssuesActions.updateIssue, (state, { issueId, changes }): IssuesState => {
    return updateIssueInMultipleLists(state, issueId, changes);
  }),

  on(IssuesActions.updateIssueSuccess, (state, { issue, changes }): IssuesState => {
    if ('description' in changes) {
      // Omit description from the server response just in case the response arrives before the next
      // debounce, which would overwrite content the user typed after the PATCH was sent.
      const { description: _omit, ...issueWithoutDescription } = issue;
      return updateIssueInMultipleLists(state, issue._id, issueWithoutDescription);
    }
    return updateIssueInMultipleLists(state, issue._id, issue);
  }),
  on(
    IssuesActions.updateIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),
  on(IssuesActions.updateTeamSuccess, (state, { issueId, teamId, teamName }): IssuesState => {
    // In the cross-team "All" view the issue is still visible after a team change —
    // keep it in place (until the next sort/reload) and just update its team.
    if (state.filters.teamId === teamAll._id) {
      return {
        ...updateIssueInMultipleLists(state, issueId, { teamId, teamName }),
        loading: LoadingState.Loaded,
      };
    }

    return {
      ...state,
      loading: LoadingState.Loaded,
      issues: issuesEntityAdapter.removeOne(issueId, state.issues),
      totalCount: state.totalCount - 1,
    };
  }),
  on(IssuesActions.setCompletedStateSuccess, (state, { issue }): IssuesState => {
    return updateIssueInMultipleLists(state, issue._id, { completed: issue.completed });
  }),
  on(
    IssuesActions.setCompletedStateFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),
  on(IssuesActions.updateUser, (state, { issueId, user }): IssuesState => {
    return updateIssueInMultipleLists(state, issueId, { userId: user._id, user: user });
  }),

  // Attachment Actions - FileAttachmentsStore handles API calls, we just update state
  on(IssuesActions.addAttachment, (state, { issueId, attachment }): IssuesState => {
    const currentAttachments = findIssueById(state, issueId)?.attachments ?? [];
    return updateIssueInMultipleLists(state, issueId, {
      attachments: [...currentAttachments, attachment],
    });
  }),

  on(IssuesActions.removeAttachment, (state, { issueId, attachment }): IssuesState => {
    const currentAttachments = findIssueById(state, issueId)?.attachments ?? [];
    return updateIssueInMultipleLists(state, issueId, {
      attachments: currentAttachments.filter(a => a._id !== attachment._id),
    });
  }),

  // Comment Actions - optimistic updates
  on(IssuesActions.addComment, (state, { issueId, comment }): IssuesState => {
    const issue = findIssueById(state, issueId);
    const currentComments = issue?.comments || [];
    return updateIssueInMultipleLists(state, issueId, {
      comments: [...currentComments, comment],
    });
  }),

  on(IssuesActions.editComment, (state, { issueId, comment }): IssuesState => {
    const issue = findIssueById(state, issueId);
    const currentComments = issue?.comments || [];
    return updateIssueInMultipleLists(state, issueId, {
      comments: currentComments.map(c => (c.createdDate === comment.createdDate ? { ...c, ...comment } : c)),
    });
  }),

  on(IssuesSubscriptionsActions.addOrUpdateCommentFromBroadcast, (state, { issueId, comment }): IssuesState => {
    const issue = findIssueById(state, issueId);
    const currentComments = issue?.comments || [];
    const index = currentComments.findIndex(c => c.userId === comment.userId && c.createdDate === comment.createdDate);
    let updatedComments: typeof currentComments;
    if (index > -1) {
      updatedComments = currentComments.map((c, i) => (i === index ? { ...c, ...comment } : c));
    } else {
      updatedComments = [...currentComments, comment];
    }

    return updateIssueInMultipleLists(state, issueId, {
      comments: updatedComments,
    });
  }),

  on(
    IssuesActions.deleteComment,
    IssuesSubscriptionsActions.deleteCommentFromBroadcast,
    (state, { issueId, comment }): IssuesState => {
      const issue = findIssueById(state, issueId);
      const currentComments = issue?.comments || [];
      return updateIssueInMultipleLists(state, issueId, {
        comments: currentComments.filter(c => !(c.createdDate === comment.createdDate && c.userId === comment.userId)),
      });
    }
  ),

  // Delete Issue
  on(
    IssuesActions.deleteIssue,
    (state, { issueId }): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
      issues: issuesEntityAdapter.removeOne(issueId, state.issues),
      totalCount: state.totalCount - 1,
    })
  ),
  on(
    IssuesActions.deleteIssueFromPB,
    (state, { issueId }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.removeOne(issueId, state.issues),
      totalCount: state.totalCount - 1,
    })
  ),
  on(
    IssuesActions.deleteIssueSuccess,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
    })
  ),

  on(
    IssuesActions.deleteIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),

  on(
    IssuesSubscriptionsActions.deleteIssueFromBroadcast,
    IssuesSubscriptionsActions.archiveIssueFromBroadcast,
    IssuesSubscriptionsActions.unarchiveIssueFromBroadcast,
    (state, { issueId }): IssuesState => {
      // Only remove and decrement count if the issue actually exists
      if (!state.issues.entities[issueId]) return state;

      return {
        ...state,
        issues: issuesEntityAdapter.removeOne(issueId, state.issues),
        totalCount: state.totalCount - 1,
      };
    }
  ),

  on(IssuesSubscriptionsActions.removeReceivedIssueFromBroadcast, (state, { issueId }): IssuesState => {
    // Remove only from received list
    return {
      ...state,
      // remove issue form sent or receive in case the user is on another team while in a meeting
      issuesReceived: issuesReceivedEntityAdapter.removeOne(issueId, state.issuesReceived),
      issuesSent: issuesSentEntityAdapter.removeOne(issueId, state.issuesSent),
    };
  }),

  // Load Sent Issues
  on(
    IssuesActions.loadSentIssues,
    (state): IssuesState => ({
      ...state,
      loadingSent: LoadingState.Loading,
    })
  ),

  // Load Received Issues
  on(
    IssuesActions.loadReceivedIssues,
    (state): IssuesState => ({
      ...state,
      loadingReceived: LoadingState.Loading,
    })
  ),

  on(
    IssuesActions.archiveIssue,
    IssuesActions.completeAndArchiveIssueSuccess,
    IssuesActions.unarchiveIssue,
    (state, { issueId }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.removeOne(issueId, state.issues),
      totalCount: state.totalCount - 1,
    })
  ),

  on(
    IssuesActions.archiveIssueFailure,
    IssuesActions.unarchiveIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),

  on(
    IssuesActions.setIssueInterval,
    (state, { issue }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.removeOne(issue._id, state.issues),
      loading: LoadingState.Loading,
      totalCount: state.totalCount - 1,
    })
  ),
  on(
    IssuesActions.setIssueIntervalSuccess,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
    })
  ),
  on(
    IssuesActions.setIssueIntervalFailure,
    (state, { issue }): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
      issues: issuesEntityAdapter.addOne(issue, state.issues),
      totalCount: state.totalCount + 1,
    })
  ),

  on(IssuesActions.addFollowerSuccess, (state, { issueId, followerIds }): IssuesState => {
    return updateIssueInMultipleLists(state, issueId, { followers: followerIds });
  }),
  on(IssuesActions.removeFollowerSuccess, (state, { issueId, followerIds }): IssuesState => {
    return updateIssueInMultipleLists(state, issueId, { followers: followerIds });
  }),

  // Filtering Actions
  on(
    IssuesActions.setTeamFilter,
    (state, { teamId }): IssuesState => ({
      ...state,
      filters: { ...state.filters, teamId },
      // The cross-team "All" view defaults to team-name order (rating desc within each team,
      // server-side); ordinals are per-team and meaningless in a merged list. Selecting a real
      // team drops the team sort back to the ordinal default.
      ...(teamId === teamAll._id && state.sortField !== 'team'
        ? { sortField: 'team' as IssueSortField, sortDirection: SortDirection.ASC }
        : {}),
      ...(teamId !== teamAll._id && state.sortField === 'team'
        ? { sortField: 'None' as IssueSortField, sortDirection: SortDirection.ASC }
        : {}),
    })
  ),
  on(
    IssuesActions.setIntervalFilter,
    (state, { intervalCode }): IssuesState => ({
      ...state,
      filters: { ...state.filters, interval: intervalCode },
    })
  ),
  on(
    IssuesActions.setArchiveFilter,
    (state, { showArchived }): IssuesState => ({
      ...state,
      filters: { ...state.filters, showArchived },
    })
  ),
  on(
    IssuesActions.setSearchFilter,
    (state, { searchText }): IssuesState => ({
      ...state,
      filters: { ...state.filters, searchText },
    })
  ),
  on(
    IssuesActions.setRatingFilter,
    (state, { rating }): IssuesState => ({
      ...state,
      filters: { ...state.filters, rating },
    })
  ),
  on(
    IssuesActions.setFilters,
    (state, { filters }): IssuesState => ({
      ...state,
      filters: { ...state.filters, ...filters },
    })
  ),
  on(
    IssuesActions.clearAllFilters,
    (state): IssuesState => ({
      ...state,
      filters: { ...issuesInitialState.filters },
    })
  ),
  on(
    IssuesActions.setSort,
    IssuesActions.setSortFromBroadcast,
    IssuesActions.initializeSort, // Handle both the same way
    (state, { field, direction }): IssuesState => ({
      ...state,
      sortField: field,
      sortDirection: direction,
    })
  ),

  // UI State Actions
  on(
    IssuesActions.changePagination,
    IssuesActions.initializePagination, // Handle both the same way
    (state, { page, pageSize }): IssuesState => ({
      ...state,
      pageIndex: page,
      pageSize,
    })
  ),

  on(
    IssuesActions.openInDetailView,
    (state, { issue }): IssuesState => ({
      ...state,
      selectedIssueId: issue ? issue._id : null,
      ui: { ...state.ui, showHistory: false },
    })
  ),

  on(
    IssuesActions.closeDetailView,
    (state): IssuesState => ({
      ...state,
      selectedIssueId: null,
      issueStandalone: null,
      ui: { ...state.ui, showHistory: false },
    })
  ),

  on(
    IssuesActions.viewAuditLog,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, showHistory: true },
    })
  ),

  on(
    IssuesActions.viewDetails,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, showHistory: false },
    })
  ),

  // Drag & Drop Actions
  on(IssuesActions.moveIssue, (state, { fromIndex, toIndex }): IssuesState => {
    //NOTE: I don't think we need to update ordinals in the state anymore, keeping this code for now
    // REASON: we don't use the ordinals in the UI, and at some point the list will be retrieved from the server
    // const offset = state.pageIndex * state.pageSize;
    // const startIndex = Math.min(fromIndex, toIndex);
    // const endIndex = Math.max(fromIndex, toIndex);

    const issuesToOrder = [...Object.values(state.issues.entities)];
    moveItemInArray(issuesToOrder, fromIndex, toIndex);

    //NOTE: I don't think we need to update ordinals in the state anymore, keeping this code for now
    //REASON: we don't use the ordinals in the UI, and at some point the list will be retrieved from the server
    // const updatedIssues = issuesToOrder.map((issue, index) =>
    //   index >= startIndex && index <= endIndex
    //     ? { ...issue, ordinal: startIndex + (index - startIndex) + offset }
    //     : issue
    // );

    return {
      ...state,
      loading: LoadingState.Loading,
      issues: issuesEntityAdapter.setAll(issuesToOrder, state.issues),
    };
  }),

  on(
    IssuesActions.moveIssueSuccess,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
      sortField: 'None',
      sortDirection: SortDirection.ASC,
    })
  ),
  on(
    IssuesActions.moveIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),

  // Team Collaboration Actions
  on(
    IssuesActions.sendToTeam,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesActions.sendToTeamSuccess,
    (state, { issue }): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
      //Note: we also reload Issues Sent
      issues: issuesEntityAdapter.removeOne(issue._id, state.issues),
      totalCount: state.totalCount - 1,
    })
  ),
  on(
    IssuesActions.sendToTeamFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
    })
  ),
  on(
    IssuesActions.sendBackToOriginalTeam,
    (state, { issue }): IssuesState => ({
      ...state,
      issues: issuesReceivedEntityAdapter.updateOne(
        {
          id: issue._id,
          changes: { workingTeamId: null, workingTeamSentBack: true },
        },
        state.issues
      ),
      issuesReceived: issuesReceivedEntityAdapter.removeOne(issue._id, state.issuesReceived),
    })
  ),
  on(
    IssuesActions.sendBackToOriginalTeamSuccess,
    (state, { issue }): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
      issuesReceived: issuesReceivedEntityAdapter.removeOne(issue._id, state.issuesReceived),
    })
  ),
  // Issues currently live in one list at a time (team board, received, or sent).
  // Send-back is initiated from issuesReceived; failure rollback only restores that list.
  // If an issue can appear in multiple lists later, also revert the optimistic issues update.
  on(
    IssuesActions.sendBackToOriginalTeamFailure,
    (state, { issue }): IssuesState => ({
      ...state,
      issuesReceived: issuesReceivedEntityAdapter.addOne(issue, state.issuesReceived),
    })
  ),
  on(
    IssuesActions.recallIssue,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),
  on(
    // Remove from the Sent panel only once the server confirms the recall; on failure the issue stays put.
    IssuesActions.recallIssueSuccess,
    (state, { issue }): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
      issuesSent: issuesSentEntityAdapter.removeOne(issue._id, state.issuesSent),
    })
  ),
  on(
    IssuesActions.recallIssueFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),
  on(
    IssuesActions.updateWorkingTeam,
    (state, { issueId, workingTeamId }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.updateOne(
        {
          id: issueId,
          changes: { workingTeamId },
        },
        state.issues
      ),
    })
  ),
  on(
    IssuesActions.revertWorkingTeam,
    (state, { issueId, originalWorkingTeamId }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.updateOne(
        {
          id: issueId,
          changes: { workingTeamId: originalWorkingTeamId },
        },
        state.issues
      ),
    })
  ),

  // Voting Actions

  // Voting Actions
  on(IssuesActions.addVote, (state, { issue, userId }): IssuesState => {
    return {
      ...state,
      userVotes: addVoteToken(state, userId, issue._id),
      issues: issuesEntityAdapter.updateOne(
        {
          id: issue._id,
          changes: {
            numOfLikes: (issue.numOfLikes ?? 0) + 1,
            likes: [...(issue.likes ?? []), userId],
            liked: true,
          },
        },
        state.issues
      ),
    };
  }),

  on(IssuesActions.removeVote, (state, { issue, userId }): IssuesState => {
    const likes = [...(issue.likes ?? [])];
    const firstIndex = likes.indexOf(userId);
    if (firstIndex > -1) {
      likes.splice(firstIndex, 1);
    }

    return {
      ...state,
      userVotes: removeVoteToken(state, userId, issue._id),
      issues: issuesEntityAdapter.updateOne(
        {
          id: issue._id,
          changes: {
            numOfLikes: likes.length,
            likes,
            liked: likes.includes(userId),
          },
        },
        state.issues
      ),
    };
  }),

  on(IssuesSubscriptionsActions.addVoteFromBroadcast, (state, { issueId, userId }): IssuesState => {
    const issue = state.issues.entities[issueId];
    // Broadcasts need to update the token cache even when the issue is off-page.
    // Per-user "liked" state is derived exclusively from userVotes in selectors —
    // never from issue.liked — so the token cache is the only place that needs updating.
    const userVotes = addVoteToken(state, userId, issueId);

    if (!issue) {
      // Early return: the vote happened on an issue that is not loaded in this page's entity list.
      return {
        ...state,
        userVotes,
      };
    }

    const newLikes = [...(issue.likes ?? []), userId];

    return {
      ...state,
      userVotes,
      // When the issue is on the current page, also mirror the raw likes array and
      // aggregate count into the visible entity so the list updates immediately from
      // realtime. liked is intentionally omitted: it is per-user state derived from
      // userVotes in selectors, not from the broadcasted userId.
      issues: issuesEntityAdapter.updateOne(
        { id: issueId, changes: { likes: newLikes, numOfLikes: newLikes.length } },
        state.issues
      ),
    };
  }),

  on(IssuesSubscriptionsActions.removeVoteFromBroadcast, (state, { issueId, userId }): IssuesState => {
    const issue = state.issues.entities[issueId];
    // Keep userVotes in sync for off-page issues for the same reason as addVoteFromBroadcast:
    // selectors derive per-user liked state from this token list, not from issue.liked.
    const userVotes = removeVoteToken(state, userId, issueId);

    if (!issue) {
      // Early return: the vote changed on another page, so only the token cache can be updated here.
      return {
        ...state,
        userVotes,
      };
    }

    const currentLikes = issue.likes ?? [];
    const index = (issue.likes ?? []).indexOf(userId);
    if (index === -1) {
      // Early return: this page does not currently show that user in the local likes array,
      // but the broadcast still needs to keep userVotes aligned with the server event.
      return {
        ...state,
        userVotes,
      };
    }

    const newLikes = [...currentLikes];
    newLikes.splice(index, 1);

    return {
      ...state,
      userVotes,
      // If the issue is visible, update the denormalized likes array and aggregate count
      // immediately so broadcast-driven counts stay in sync without reloading. liked is
      // intentionally omitted: it is per-user state derived from userVotes in selectors,
      // not from the broadcasted userId.
      issues: issuesEntityAdapter.updateOne(
        { id: issueId, changes: { likes: newLikes, numOfLikes: newLikes.length } },
        state.issues
      ),
    };
  }),

  // UI State Reducers
  on(
    IssuesActions.setSelectedIssueList,
    (state, { listType }): IssuesState => ({
      ...state,
      ui: { ...state.ui, selectedIssueList: listType },
    })
  ),
  on(
    IssuesActions.setIsSentToOtherTeam,
    (state, { isSentToOtherTeam }): IssuesState => ({
      ...state,
      ui: { ...state.ui, isSentToOtherTeam },
    })
  ),
  on(
    IssuesActions.toggleTopThreeSelection,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, isSelectingTopThree: !state.ui.isSelectingTopThree },
    })
  ),
  on(
    IssuesActions.setIssueToMerge,
    (state, { issue }): IssuesState => ({
      ...state,
      ui: { ...state.ui, issueToMerge: issue },
    })
  ),
  on(
    IssuesActions.mergeIssues,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesActions.mergeIssuesSuccess,
    (state, { completedIssue, mergedIssue }): IssuesState => ({
      ...state,
      loading: LoadingState.Loaded,
      // Mark source issue as completed and update target issue
      issues: issuesEntityAdapter.updateMany(
        [
          { id: completedIssue._id, changes: completedIssue },
          { id: mergedIssue._id, changes: mergedIssue },
        ],
        state.issues
      ),
      // Clear merge state
      ui: { ...state.ui, issueToMerge: null },
    })
  ),
  on(
    IssuesActions.mergeIssuesFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),
  on(
    IssuesActions.cancelMerge,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, issueToMerge: null },
      loading: LoadingState.NotLoading,
    })
  ),
  on(
    IssuesActions.setListView,
    (state, { listView }): IssuesState => ({
      ...state,
      ui: { ...state.ui, listView },
    })
  ),
  on(
    IssuesActions.initializeListView,
    (state, { listView }): IssuesState => ({
      ...state,
      ui: { ...state.ui, listView },
    })
  ),
  on(
    IssuesActions.toggleShowMetrics,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, showMetrics: !state.ui.showMetrics },
    })
  ),
  on(
    IssuesActions.setCanShowMetrics,
    (state, { canShowMetrics }): IssuesState => ({
      ...state,
      ui: { ...state.ui, canShowMetrics },
    })
  ),
  on(
    IssuesActions.setIsFilteredList,
    (state, { isFiltered }): IssuesState => ({
      ...state,
      ui: { ...state.ui, isFilteredList: isFiltered },
    })
  ),
  on(
    IssuesActions.teamIssuesComponentLoaded,
    (state): IssuesState => ({
      ...state,
      ui: { ...state.ui, teamIssuesComponentLoaded: true },
    })
  ),
  on(IssuesActions.clearIssuesState, (): IssuesState => issuesInitialState),
  on(RocksIssuesEvents.clearIssuesState, (): IssuesState => issuesInitialState),
  // The list component is destroyed when trash replaces it in the page; keep the slice so trash
  // (which shares it) isn't wiped. `hideTrashItems` resets the slice when trash closes.
  on(
    IssuesActions.teamIssuesComponentDestroyed,
    (state): IssuesState => (state.showTrash ? state : issuesInitialState)
  ),

  // Context Menu Actions
  on(
    IssuesActions.openContextMenu,
    (state, { position, issue, index, sentOrReceived }): IssuesState => ({
      ...state,
      ui: {
        ...state.ui,
        contextMenu: {
          isOpen: true,
          position,
          issue,
          index,
          sentOrReceived,
        },
      },
    })
  ),
  on(
    IssuesActions.closeContextMenu,
    (state): IssuesState => ({
      ...state,
      ui: {
        ...state.ui,
        contextMenu: {
          ...state.ui.contextMenu,
          isOpen: false,
        },
      },
    })
  ),

  // Inline Create Actions
  on(IssuesActions.addInline, (state, { issue }): IssuesState => {
    return {
      ...state,
      issues: issuesEntityAdapter.addOne(issue, state.issues),
      ui: {
        ...state.ui,
        focusOnInlineAdd: false,
        addNewDisabled: true,
      },
    };
  }),
  on(IssuesActions.cancelAddInline, (state): IssuesState => {
    return {
      ...state,
      issues: issuesEntityAdapter.removeOne(INLINE_TEMP_ISSUE_ID, state.issues),
      ui: {
        ...state.ui,
        addNewDisabled: false,
        focusOnInlineAdd: true,
      },
    };
  }),
  on(IssuesActions.inlineTeamSelected, (state, { teamId }): IssuesState => {
    return {
      ...state,
      issues: issuesEntityAdapter.updateOne({ id: INLINE_TEMP_ISSUE_ID, changes: { teamId } }, state.issues),
    };
  }),
  on(
    IssuesActions.updateInlineDraftTitle,
    IssuesActions.inlineDraftTitleSaveRequested,
    (state, { title }): IssuesState => {
      return {
        ...state,
        issues: issuesEntityAdapter.updateOne({ id: INLINE_TEMP_ISSUE_ID, changes: { title } }, state.issues),
      };
    }
  ),
  on(IssuesActions.saveInlineSuccess, (state, { issue }): IssuesState => {
    // Remove temporary and add the created issue
    let updatedIssues = issuesEntityAdapter.removeOne(INLINE_TEMP_ISSUE_ID, state.issues);
    updatedIssues = issuesEntityAdapter.addOne(issue, updatedIssues);

    return {
      ...state,
      issues: updatedIssues,
      totalCount: state.totalCount + 1,
      ui: {
        ...state.ui,
        focusOnInlineAdd: true,
        addNewDisabled: false,
      },
    };
  }),
  on(IssuesActions.saveInlineFailed, (state): IssuesState => {
    return {
      ...state,
      issues: issuesEntityAdapter.removeOne(INLINE_TEMP_ISSUE_ID, state.issues),
      ui: {
        ...state.ui,
        addNewDisabled: false,
        focusOnInlineAdd: false,
      },
    };
  }),

  on(
    IssuesActions.loadLongTermIssuesForSltVto,
    (state, { teamId }): IssuesState => ({
      ...state,

      filters: {
        ...state.filters,
        teamId: teamId,
        onlyPublic: true,
        interval: IntervalCode.longTerm,
      },
      ui: {
        ...state.ui,
        isViewingSltVto: true,
        isViewingArchivedVto: false,
        archivedVtoId: null,
      },
    })
  ),
  on(
    IssuesActions.loadLongTermIssuesForTeamVto,
    (state, { teamId }): IssuesState => ({
      ...state,
      filters: {
        ...state.filters,
        teamId: teamId,
        onlyPublic: false,
        interval: IntervalCode.longTerm,
      },
      ui: {
        ...state.ui,
        isViewingArchivedVto: false,
        isViewingSltVto: false,
        archivedVtoId: null,
      },
    })
  ),
  on(
    IssuesActions.viewArchivedVto,
    (state, { vto }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.setAll(vto.issues, state.issues),
      totalCount: vto.issues.length,
      ui: {
        ...state.ui,
        isViewingArchivedVto: true,
        isViewingSltVto: false,
        archivedVtoId: vto._id,
      },
    })
  ),
  on(
    IssuesActions.setReorderedIssues,
    (state, { issues }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.setAll(issues, state.issues),
      sortField: 'None',
      sortDirection: null,
    })
  ),

  sortIssuesLocalAndUpdateOrdinalsReducer,
  sortIssuesLocalFromBroadcastReducer,
  sortIssuesLocalOnlyReducer,
  updateOrdinalsOnDragDropReducer,

  on(
    LayoutsIssuesEvents.showTrashItems,
    (): IssuesState => ({
      ...issuesInitialState,
      showTrash: true,
      loading: LoadingState.Loading,
    })
  ),
  on(LayoutsIssuesEvents.hideTrashItems, (): IssuesState => issuesInitialState),
  on(
    IssuesTrashPageActions.paginationChanged,
    (state, { pageIndex, pageSize }): IssuesState => ({
      ...state,
      pageIndex,
      pageSize,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashPageActions.sortChanged,
    (state, { sortField, sortDirection }): IssuesState => ({
      ...state,
      trashSortField: sortField,
      trashSortDirection: sortDirection,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashPageActions.searchChanged,
    (state, { searchText }): IssuesState => ({
      ...state,
      filters: { ...state.filters, searchText },
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashPageActions.teamChanged,
    (state): IssuesState => ({
      ...state,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashPageActions.usersChanged,
    (state): IssuesState => ({
      ...state,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashPageActions.refreshed,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesTrashApiActions.loadTrashSuccess,
    (state, { rows, totalCount }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.setAll(rows, state.issues),
      totalCount,
      loading: LoadingState.Loaded,
    })
  ),
  on(
    IssuesTrashApiActions.loadTrashFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  ),
  on(
    IssuesTrashApiActions.restoreSuccess,
    IssuesTrashApiActions.deleteForeverSuccess,
    (state, { id }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.removeOne(id, state.issues),
      totalCount: Math.max(0, state.totalCount - 1),
    })
  ),

  // The Archive tab is a sibling route of the live lists and shares this slice, so entering it
  // starts from a clean state — otherwise paging/sort carry over from whichever tab you left.
  on(
    IssuesArchivePageActions.opened,
    (): IssuesState => ({
      ...issuesInitialState,
      showArchiveTab: true,
      loading: LoadingState.Loading,
    })
  ),
  on(IssuesArchivePageActions.closed, (): IssuesState => issuesInitialState),
  on(
    IssuesArchivePageActions.paginationChanged,
    (state, { pageIndex, pageSize }): IssuesState => ({
      ...state,
      pageIndex,
      pageSize,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchivePageActions.sortChanged,
    (state, { sortField, sortDirection }): IssuesState => ({
      ...state,
      archiveSortField: sortField,
      archiveSortDirection: sortDirection,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchivePageActions.typeFilterChanged,
    (state, { intervalCode }): IssuesState => ({
      ...state,
      archiveTypeFilter: intervalCode,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchivePageActions.searchChanged,
    (state, { searchText }): IssuesState => ({
      ...state,
      filters: { ...state.filters, searchText },
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchivePageActions.teamChanged,
    IssuesArchivePageActions.usersChanged,
    (state): IssuesState => ({
      ...state,
      pageIndex: 0,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchivePageActions.refreshed,
    IssuesArchivePageActions.retryRequested,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Loading,
    })
  ),
  on(
    IssuesArchiveApiActions.loadArchiveSuccess,
    (state, { rows, totalCount }): IssuesState => ({
      ...state,
      issues: issuesEntityAdapter.setAll(rows, state.issues),
      totalCount,
      loading: LoadingState.Loaded,
    })
  ),
  on(
    IssuesArchiveApiActions.loadArchiveFailure,
    (state): IssuesState => ({
      ...state,
      loading: LoadingState.Error,
    })
  )
);
